import { TaskLinkEntity } from './task.props.type';

import * as Enumerable from 'linq';
import { DailyPhaseName, TaskGroupBy, TaskLinkType, TaskPhaseName, TaskStatus, TaskTarget } from './task.enum';
import { getEntityTaskLink, getPhaseTag, JobTaskPhases } from './task.utility';
import { NestedTask, Task } from './task';
import { v4 } from 'uuid';
import { LabelEntity, LabelType } from '../shared/label';
import { TaskViewOptions, defaultViewOptions } from './task.options';
import { ContactEntity } from '../contact/contact.options';
import { FiltersRelativeDateUtility, FiltersRelativeDateMethod } from '../../utilities/filters.relative.date.utility';
import { DateUtility } from '../../utilities/date.utility';
import { LexoRank } from '../shared/lexorank';

export class TaskGroupUtility {

    public static noLocation = "No Location";

    public static createNestedTask(task: Task, currentGroupBy: TaskGroupBy): NestedTask {

        const groupLinks: Record<TaskLinkType, TaskLinkEntity> = {} as any;

        if (task.assignee) {
            groupLinks[TaskLinkType.Assignee] = getEntityTaskLink({ ...task.assignee, data: task.assignee?.name ?? "" }, TaskLinkType.Assignee);
        }

        if (task?.data?.location) {
            const location = task.data.location.toUpperCase();
            groupLinks[TaskLinkType.Location] = getEntityTaskLink({ _id: location, name: location, data: location }, TaskLinkType.Location);
        }

        if (task.target) {
            groupLinks[TaskLinkType.Target] = getEntityTaskLink({ _id: task.target, name: task.target, data: task.target }, TaskLinkType.Type);
        }

        if (task.type) {
            groupLinks[TaskLinkType.Type] = getEntityTaskLink({ _id: task.type, name: task.type, data: task.type }, TaskLinkType.Type);
        }

        if (task.status) {
            groupLinks[TaskLinkType.Status] = getEntityTaskLink({ _id: task.status, name: task.status, data: task.status }, TaskLinkType.Type);
        }
        else {
            groupLinks[TaskLinkType.Status] = getEntityTaskLink({ _id: TaskStatus.Open, name: TaskStatus.Open, data: TaskStatus.Open }, TaskLinkType.Type);
        }

        Enumerable
            .from(task.links as TaskLinkEntity[])
            .forEach(x => {
                groupLinks[x.type] = x;
            });

        Enumerable
            .from(task.tags)
            .forEach(x => {

                switch (x.type) {
                    case LabelType.Phase:
                        groupLinks[x._id] = getEntityTaskLink({ ...x, data: x.name ?? "" }, TaskLinkType.Phase);
                        groupLinks[TaskLinkType.Phase] = getEntityTaskLink({ ...x, data: x.name ?? "" }, TaskLinkType.Phase);
                        return;

                    case LabelType.File:
                        groupLinks[x._id] = getEntityTaskLink({ ...x, data: x.name ?? "" }, TaskLinkType.Tags);
                        return;

                    default:
                        groupLinks[x.type] = getEntityTaskLink({ ...x, data: x.name ?? "" }, TaskLinkType[x.type]);
                }
            });

        const newTask = {
            ...task,
            groupLinks,
            children: [],
            currentGroupBy: currentGroupBy,
        } as NestedTask;

        return newTask;
    }

    public static build(tasks: Task[], options: TaskViewOptions, includesDailyGroup: boolean = true, linkLevel?: TaskLinkEntity): NestedTask[] {

        const safeOptions = options ?? defaultViewOptions;
        let result: NestedTask[];

        const start = new Date();
        //console.log((new Date()).getTime() - start.getTime());

        switch (safeOptions.groupBy) {
            case TaskGroupBy.Stage:
                result = this.groupByStage(tasks, safeOptions);
                break;
            case TaskGroupBy.Phase:
                result = this.groupByPhase(tasks, safeOptions, includesDailyGroup, linkLevel);
                break;
            case TaskGroupBy.Assignee:
                result = this.groupByAssignee(tasks, safeOptions);
                break;
            case TaskGroupBy.Tags:
                result = this.groupByTags(tasks, safeOptions);
                break;
            case TaskGroupBy.Location:
                result = this.groupByField(TaskLinkType.Location, tasks, safeOptions, this.createGroupTask(TaskGroupBy.Location, this.noLocation, this.noLocation), null);
                break;
            case TaskGroupBy.Target:
                const targetMap = new Map<string, NestedTask>([TaskTarget.Field, TaskTarget.Office, TaskTarget.Supervisor].map(e => [e, null]));
                result = this.groupByField(TaskLinkType.Target, tasks, safeOptions, this.createGroupTask(TaskGroupBy.Target, "No Target", "No Target"), targetMap);
                break;
            case TaskGroupBy.Type:
                result = this.groupByField(TaskLinkType.Type, tasks, safeOptions, this.createGroupTask(TaskGroupBy.Type, "No Type", "No Type"), null);
                break;
            case TaskGroupBy.Status:
                // Every status the set defines, so E1-04's three new ones appear as their own columns.
                const statusMap = new Map<string, NestedTask>(Object.values(TaskStatus).map(e => [e, null]));
                result = this.groupByField(TaskLinkType.Status, tasks, safeOptions, this.createGroupTask(TaskGroupBy.Status, "No Status", "No Status"), statusMap);
                break;
            default:
                result = this.groupByNone(tasks, safeOptions);
                break;
        }

        //console.log((new Date()).getTime() - start.getTime());
        if (!safeOptions.showEmptyGroups) {

            result = Enumerable
                .from(result)
                .where(e => {

                    if (e.type === TaskLinkType.Group) {

                        if ([DailyPhaseName.CheckIn, DailyPhaseName.CheckOut].includes(e._id as any)) {
                            // We are dealing with the daily task groups adn we need to check the children have children
                            e.children = e.children.filter(x => x.children.length > 0);
                        }

                        return e.children.length > 0;
                    }

                    return true;

                })
                .toArray();
        }


        return result;
    }

    private static groupByStage(tasks: Task[], options: TaskViewOptions): NestedTask[] {

        let rootParents = [];
        const taskMap = new Map(tasks.map(e => [e._id, this.createNestedTask(e, TaskGroupBy.Stage)]))
        const parents = new Map<string, NestedTask>(tasks.filter(e => !!e.parent).map(e => [e.parent._id, taskMap.get(e.parent._id)]));

        for (const task of taskMap.values()) {
            if (!task.parent) {
                rootParents.push(task);
                continue;
            }

            let parent = parents.get(task.parent._id);
            if (!parent) {
                continue;
            }

            if (!parent?.children) {
                parent.children = [];
            }

            parent.children.push(task);
        }

        this.filterAndSortChildInParents([...Array.from(parents.values()), ...Array.from(parents.values())], options);

        const groups: NestedTask[] = [];

        if (options.sortBy === TaskGroupBy.Stage) {
            rootParents = this.sortByStage(rootParents, options);
        } else {
            rootParents = this.sortBySortOrder(rootParents, options);
        }

        rootParents = this.prioritizeJobParents(rootParents);

        for (const parent of rootParents) {
            const id = parent._id;
            if (!taskMap.has(id)) {
                continue;
            }

            groups.push({
                ...taskMap.get(id),
                currentGroupBy: TaskGroupBy.Stage
            });
        }

        return groups;
    }

    private static groupByPhase(tasks: Task[], options: TaskViewOptions, includesDailyGroup: boolean = true, linkLevel: TaskLinkEntity): NestedTask[] {

        const addDailyToStage = linkLevel?.type === TaskLinkType.Job
        const virtualStageTasks: Map<string, NestedTask> = new Map<string, NestedTask>();

        let enumerable = Enumerable.empty<NestedTask>();
        if (addDailyToStage && includesDailyGroup) {
            // create stage tasks in daily group
            enumerable = Enumerable.from(tasks).where(x => x.type !== TaskLinkType.Job)
                .selectMany(e => {
                    if (e.type === TaskLinkType.WorkOrder) {
                        const stagesInDaily = [DailyPhaseName.CheckIn, DailyPhaseName.CheckOut].map(x => {
                            const fake = this.createNestedTask(e, TaskGroupBy.Phase)
                            fake._id = `${e._id}+${x}`;
                            fake.children = [];
                            fake.parent = {
                                _id: x,
                                name: x,
                                type: TaskLinkType.Phase
                            }
                            return fake
                        });

                        for (const item of stagesInDaily) {
                            virtualStageTasks.set(item._id, item);
                        }

                        return stagesInDaily;
                    }

                    return [this.createNestedTask(e, TaskGroupBy.Phase)];
                });
        }
        else {
            enumerable = Enumerable.from(tasks).where(x => !x.virtual).select(e => this.createNestedTask(e, TaskGroupBy.Phase));
        }

        const taskList = enumerable.toArray();
        const noPhaseTasks = this.createGroupTask(TaskGroupBy.Phase, "No Phase", "No Phase");
        const parents = new Map<string, NestedTask>(taskList.map(e => [e._id, e]));
        const phaseName: string[] = [TaskPhaseName.Before, TaskPhaseName.During, TaskPhaseName.After];

        if (includesDailyGroup) {
            phaseName.push(DailyPhaseName.CheckIn, DailyPhaseName.CheckOut);
        }

        const phases = new Map<string, NestedTask>(phaseName
            .map(e => {
                const phase = this.createGroupTask(TaskGroupBy.Phase, e, e, getPhaseTag(e), null);
                parents.set(e, phase);
                return [e, phase];
            }));

        const foundParents = new Map<string, NestedTask>();

        for (const task of taskList) {

            let parent = parents.get(task.parent._id);
            if (parent) {
                if (!parent?.children) {
                    parent.children = [];
                }

                parent.children.push(task);
                if (task.type === TaskLinkType.Compliance) {
                    parent.hasComplianceSubtask = true;
                }

                if (!foundParents.has(parent._id)) {
                    foundParents.set(parent._id, parent);
                }

                continue;
            }

            let inGroup = false;
            for (const key of phases.keys()) {
                const tag = task.groupLinks[key];
                if (!tag) {
                    continue;
                }

                inGroup = true;
                const group = phases.get(key)

                if (virtualStageTasks.size > 0 && (group._id === DailyPhaseName.CheckIn || group._id === DailyPhaseName.CheckOut)) {
                    const found = virtualStageTasks.get(`${task.parent._id}+${group._id}`);
                    if (found) {
                        found.children.push(task);
                        continue;
                    }
                }

                // Set the group by as the parent for the task. this is use on field app
                task.virtualParent = { ...group, children: [] };
                group.children.push(task);

                if (!foundParents.has(group._id)) {
                    foundParents.set(group._id, group);
                }
            }

            if (!inGroup) {
                task.virtualParent = { ...noPhaseTasks, children: [] };
                noPhaseTasks.children.push(task);
            }
        }

        this.filterAndSortChildInParents([...(Array.from(foundParents.values())), ...(Array.from(phases.values())), noPhaseTasks], options);
        let available = null;
        if (!options.filterValue) {
            available = Array.from(phases.values());
        } else {
            available = this.getAvailableParent(Array.from(phases.values()));
        }

        if (noPhaseTasks.children.length === 0) {
            return available;
        }

        return [...available, noPhaseTasks];
    }

    private static groupByAssignee(tasks: Task[], options: TaskViewOptions): NestedTask[] {

        let assignees: NestedTask[] = [];
        const noVirtual = Enumerable
            .from(tasks)
            .where(x => {
                // if (x.daily) {
                //     return x.instance;
                // }

                return !x.virtual
            })
            .select(e => {
                if (e.assignee) {
                    assignees.push(this.createGroupTask(TaskGroupBy.Assignee, e.assignee.name, e.assignee._id, null, e.assignee));
                }

                return this.createNestedTask(e, TaskGroupBy.Assignee)
            })
            .toArray();

        const enumerable = Enumerable.from(assignees).distinct(e => e._id);
        if (options.sortOrder === 'asc') {
            assignees = enumerable.orderBy(e => e.name).toArray();
        }
        else {
            assignees = enumerable.orderByDescending(e => e.name).toArray();
        }

        const assigneeMap = new Map<string, NestedTask>(assignees.map(e => [e._id, e]));
        const noAssigneeTasks = this.createGroupTask(TaskGroupBy.Assignee, "No Assignee", "No Assignee");
        const parents = new Map<string, NestedTask>(noVirtual.map(e => [e._id, e]));

        for (const task of noVirtual) {
            const assignee = task.groupLinks[TaskLinkType.Assignee]?._id;
            const found = assigneeMap.get(assignee);
            if (found) {
                this.addGroupedTask(found, task, parents);
            }
            else {
                this.addGroupedTask(noAssigneeTasks, task, parents);
            }
        }

        let groups: NestedTask[];
        if (noAssigneeTasks.children.length === 0) {
            groups = Array.from(assigneeMap.values());
        }
        else {
            groups = [...Array.from(assigneeMap.values()), noAssigneeTasks];
        }

        this.filterAndSortChildInParents(groups, options);

        return groups.filter(e => !!e.children?.length);
    }

    private static groupByNone(tasks: Task[], options: TaskViewOptions): NestedTask[] {

        const noVirtual = Enumerable
            .from(tasks)
            .where(x => !x.virtual)
            .select(e => this.createNestedTask(e, TaskGroupBy.None))
            .toArray();

        const taskMap = new Map<string, NestedTask>(noVirtual.map(e => [e._id, e]));
        const foundParent = new Map<string, NestedTask>();

        for (const task of noVirtual) {

            let parent = taskMap.get(task.parent._id);

            if (!parent) {
                continue;
            }

            taskMap.delete(task._id);

            if (!parent?.children) {
                parent.children = [];
            }

            parent.children.push(task);

            if (!foundParent.has(parent._id)) {
                foundParent.set(parent._id, parent);
            }
        }

        const temp = { children: Array.from(taskMap.values()) } as NestedTask;
        this.filterAndSortChildInParents([...Array.from(foundParent.values()), temp], options);

        const items = temp.children;;

        return items;
    }

    private static groupByTags(tasks: Task[], options: TaskViewOptions): NestedTask[] {

        const tagGroups = new Map<string, NestedTask>();

        const noVirtual = Enumerable
            .from(tasks)
            .where(x => {
                // if (x.daily) {
                //     return x.instance;
                // }

                return !x.virtual
            })
            .select(e => {
                const nested = this.createNestedTask(e, TaskGroupBy.Tags);

                if (e.tags?.length > 0) {
                    for (const tag of e.tags) {
                        if (tag.type !== LabelType.File || tagGroups.has(tag._id)) {
                            continue;
                        }

                        tagGroups.set(tag._id, this.createGroupTask(TaskGroupBy.Tags, tag.name, tag.name, tag, null));
                    }
                }

                return nested;
            })
            .toArray();

        const noTagTasks = this.createGroupTask(TaskGroupBy.Tags, "No Tag", "No Tag",);
        const parents = new Map(noVirtual.map(e => [e._id, e]));

        for (const task of noVirtual) {
            let inGroup = false;
            for (const key of tagGroups.keys()) {
                const tag = task.groupLinks[key];
                if (!tag) {
                    continue;
                }

                inGroup = true;
                const group = tagGroups.get(key)
                this.addGroupedTask(group, task, parents);
            }

            if (!inGroup) {
                this.addGroupedTask(noTagTasks, task, parents);
            }
        }

        let groups: NestedTask[];

        if (noTagTasks.children.length === 0) {
            groups = Array.from(tagGroups.values());
        }
        else {
            groups = [...Array.from(tagGroups.values()), noTagTasks];
        }

        this.filterAndSortChildInParents(groups, options);


        return groups;
    }

    private static addGroupedTask(group: NestedTask, task: NestedTask, parents: Map<string, NestedTask>) {
        const foundParent = parents.get(task.parent?._id);
        if (foundParent) {
            task.fullParent = foundParent;
        }

        const container = foundParent
            ? this.ensureGroupedParent(group, foundParent, parents)
            : group;

        if (!container.children.find(x => x._id === task._id)) {
            container.children.push(task);
        }
    }

    private static ensureGroupedParent(group: NestedTask, parent: NestedTask, parents: Map<string, NestedTask>): NestedTask {
        const directParent = parents.get(parent.parent?._id);
        const host = directParent
            ? this.ensureGroupedParent(group, directParent, parents)
            : group;

        let existing = host.children.find(x => x._id === parent._id);
        if (!existing) {
            existing = {
                ...parent,
                children: []
            } as NestedTask;
            host.children.push(existing);
        }

        return existing;
    }

    private static groupByField(groupByField: TaskLinkType, tasks: Task[], options: TaskViewOptions, emptyGroup: NestedTask, groupByFieldMap: Map<string, NestedTask>): NestedTask[] {
        if (!groupByFieldMap) {
            groupByFieldMap = new Map<string, NestedTask>();
        }

        const noVirtual = Enumerable
            .from(tasks)
            .where(x => !x.virtual)
            .select(e => {
                return this.createNestedTask(e, TaskGroupBy[groupByField])
            })
            .select(e => {
                const field = e.groupLinks[groupByField];

                if (field && !groupByFieldMap.get(field._id)) {

                    const fieldTask =
                        {
                            ...field,
                            children: [],
                            type: TaskLinkType.Group,
                            currentGroupBy: TaskGroupBy[groupByField]
                        } as NestedTask;

                    if (groupByField === TaskLinkType.Target) {
                        fieldTask['target'] = TaskTarget[field._id];
                    }

                    groupByFieldMap.set(field._id, fieldTask)
                }

                return e;
            })
            .toArray();

        const parents = new Map<string, NestedTask>(noVirtual.map(e => [e._id, e]));
        const foundParents = new Map<string, NestedTask>();

        for (const task of noVirtual) {
            let parent = parents.get(task.parent._id);
            if (parent) {
                parents.delete(task._id);

                if (!parent?.children) {
                    parent.children = [];
                }

                parent.children.push(task);
                if (!foundParents.has(parent._id)) {
                    foundParents.set(parent._id, parent);
                }
                continue;
            }

            const value = task.groupLinks[groupByField]?._id;
            const found = groupByFieldMap.get(value);
            if (found) {
                found.children.push(task);
            }
            else {
                emptyGroup.children.push(task);
            }
        }

        const groups = [...(Array.from(groupByFieldMap.values())).filter(e => !!e?.children?.length), emptyGroup];
        this.filterAndSortChildInParents([...(Array.from(foundParents.values())), ...groups], options);

        return groups.filter(e => !!e.children?.length);
    }


    private static getAvailableParent(parents: NestedTask[]) {
        const available = [];

        for (const item of parents) {
            const isChildrenNotEmpty = this.isChildrenNotEmpty(item);
            if (isChildrenNotEmpty) {
                available.push(isChildrenNotEmpty);
            }
        }

        return available;
    }

    private static isChildrenNotEmpty(parent: NestedTask): NestedTask {
        if (parent.type === TaskLinkType.Group || parent.virtual === true || (parent.daily && !parent.instance)) {
            if (!parent.children?.length) {
                return null;
            }

            const available = [];
            for (const child of parent.children) {

                const notNull = this.isChildrenNotEmpty(child);
                if (notNull) {
                    available.push(notNull);
                }
            }

            if (available.length > 0) {
                parent.children = available;
                return parent;
            }

            return null;

        }

        return parent;
    }

    private static createGroupTask(currentGroupBy: TaskGroupBy, name: string, id: string = null, tag: LabelEntity = null, assignee: ContactEntity = null): NestedTask {
        return {
            name: name,
            _id: id ?? v4(),
            type: TaskLinkType.Group,
            tags: !tag ? null : [tag],
            assignee: assignee,
            children: [],
            currentGroupBy: currentGroupBy
        } as NestedTask;
    }

    private static filterAndSortChildInParents(parents: NestedTask[], taskViewOptions: TaskViewOptions) {
        if (!parents?.length) {
            return [];
        }

        let sortField = TaskLinkType[taskViewOptions.sortBy];
        if (taskViewOptions.sortBy === TaskGroupBy.None) {
            sortField = 'sortOrder';
        }

        const getSortValue = (task: NestedTask) => {
            if (taskViewOptions.sortBy === TaskGroupBy.None) {
                return task.sortOrder ?? '';
            }

            return task.groupLinks[sortField]?.data ?? '';
        }

        for (const parent of parents) {
            if (!parent?.children) {
                continue;
            }

            let tasks = [...parent.children];

            if (taskViewOptions.filterValue) {
                tasks = this.filterTaskHierarchy(tasks, taskViewOptions);
            }

            // sort work orders under check in / check out group when group by phase
            if ((parent.name === DailyPhaseName.CheckIn || parent.name === DailyPhaseName.CheckOut) && parent.currentGroupBy === TaskGroupBy.Phase) {
                parent.children = this.sortTasks(tasks, (a, b) => this.compareLexoRank(a.sortOrder, b.sortOrder));
                continue;
            }

            // wont sort if the sort by is stage
            if (taskViewOptions.sortBy === TaskGroupBy.Stage) {
                parent.children = tasks;
                continue;
            }

            parent.children = this.sortTasks(tasks, (a, b) => {
                const compareValue = taskViewOptions.sortBy === TaskGroupBy.None
                    ? this.compareLexoRank(a.sortOrder, b.sortOrder)
                    : taskViewOptions.sortBy === TaskGroupBy.Phase
                        ? this.comparePhase(getSortValue(a), getSortValue(b))
                        : this.compareString(getSortValue(a), getSortValue(b));

                if (taskViewOptions.sortOrder === 'desc') {
                    return compareValue * -1;
                }

                return compareValue;
            });
        }
    }

    private static filterTaskHierarchy(tasks: NestedTask[], taskViewOptions: TaskViewOptions): NestedTask[] {
        return tasks.filter(task => {
            if (task.children?.length) {
                task.children = this.filterTaskHierarchy(task.children, taskViewOptions);
            }

            return task._id === taskViewOptions.alwaysIncludeTaskId
                || this.filter(task, taskViewOptions)
                || task.children?.length > 0;
        });
    }

    private static sortByStage(parents: NestedTask[], taskViewOptions: TaskViewOptions): NestedTask[] {
        if (taskViewOptions.sortOrder === 'asc') {
            parents = Enumerable.from(parents)
                .orderBy(e => {
                    if (e.type === TaskLinkType.WorkOrder) {
                        // return e.name;
                        return e.sortOrder;
                    }

                    return ' ';
                }).toArray();
        }
        else {
            parents = Enumerable.from(parents)
                .orderByDescending(e => {
                    if (e.type === TaskLinkType.WorkOrder) {
                        // return e.name;
                        return e.sortOrder;
                    }

                    return ' ';
                }).toArray();
        }

        return parents;
    }

    private static sortBySortOrder(parents: NestedTask[], taskViewOptions: TaskViewOptions): NestedTask[] {
        return this.sortTasks(parents, (a, b) => {
            const compareValue = this.compareLexoRank(a.sortOrder, b.sortOrder);

            if (taskViewOptions.sortOrder === 'desc') {
                return compareValue * -1;
            }

            return compareValue;
        });
    }

    private static prioritizeJobParents(parents: NestedTask[]): NestedTask[] {
        if (!parents?.length) {
            return parents;
        }

        const jobs = parents.filter(parent => parent.type === TaskLinkType.Job);
        const others = parents.filter(parent => parent.type !== TaskLinkType.Job);

        return [...jobs, ...others];
    }

    private static sortTasks(tasks: NestedTask[], compare: (a: NestedTask, b: NestedTask) => number): NestedTask[] {
        return tasks
            .map((task, index) => ({ task, index }))
            .sort((left, right) => {
                const compareValue = compare(left.task, right.task);
                if (compareValue !== 0) {
                    return compareValue;
                }

                return left.index - right.index;
            })
            .map(({ task }) => task);
    }

    private static compareLexoRank(left?: string, right?: string): number {
        if (left === right) {
            return 0;
        }

        if (!left) {
            return 1;
        }

        if (!right) {
            return -1;
        }

        try {
            const leftRank = LexoRank.from(left);
            const rightRank = LexoRank.from(right);

            if (leftRank.lessThan(rightRank)) {
                return -1;
            }

            if (rightRank.lessThan(leftRank)) {
                return 1;
            }
        }
        catch {
            return this.compareString(left, right);
        }

        return 0;
    }

    private static compareString(left: string, right: string): number {
        return left.localeCompare(right);
    }

    // Phases have a semantic order (Check-In, Before, During, After, Check-Out) so alphabetical comparison is wrong
    private static comparePhase(left: string, right: string): number {

        const order = JobTaskPhases.map(x => x.name as string);
        const getIndex = (phase: string) => {
            const index = phase ? order.indexOf(phase) : -1;
            return index === -1 ? order.length : index;
        };

        return getIndex(left) - getIndex(right);
    }

    private static filter(task: NestedTask, taskViewOptions: TaskViewOptions): boolean {
        if (!taskViewOptions.filterValue) {
            return true;
        }

        const filterValue = taskViewOptions.filterValue;
        const result: boolean[] = [];
        for (const field in filterValue) {
            const selectedValue = filterValue[field];

            if (field !== 'daily') {
                if (selectedValue == null || selectedValue === '' || (Array.isArray(selectedValue) && selectedValue.length === 0)) {
                    result.push(true);
                    continue;
                }

                if (typeof selectedValue === 'boolean') {
                    result.push(Boolean(task[field]) === selectedValue);
                    continue;
                }

                if (task[field] == null) {
                    result.push(false);
                    continue;
                }

            }

            let match: boolean = true;
            switch (field) {
                case 'tags': {

                    const arr = filterValue[field] as any[];
                    if (arr.length === 0) {
                        break;
                    }

                    const arrFieldValue = task[field] as LabelEntity[];
                    match = arr.some(e => arrFieldValue.findIndex(l => l._id === e._id) > -1);
                    result.push(match);
                    break;
                }

                case 'assignee':
                case 'completedBy':
                case 'fileTemplate':
                case 'formTemplate': {
                    const arr = filterValue[field] as any[];
                    if (arr.length === 0) {
                        break;
                    }

                    match = arr.some(e => e._id === task[field]?._id);
                    result.push(match);
                    break;
                }

                case "completedOn":
                case 'dueDate': {

                    // dueDate/completedOn represent a calendar day, not a precise instant, so compare on
                    // day granularity in the filter's timezone (the zone the range was resolved in). This
                    // keeps both ends of a Between range inclusive and stops a stray time-of-day from
                    // dropping a task that falls on a boundary day.
                    //
                    // Verified (filter tz Australia/Sydney; UTC-midnight and tenant-local storage behave alike):
                    //   Between 16–20 → due 15 hidden, 16 shown, 20 shown, 21 hidden (inclusive both ends)
                    //   On 16 → only 16; OnOrBefore 16 → ≤ 16 (incl. 16); OnOrAfter 16 → ≥ 16 (incl. 16)
                    //   Today → today shown, tomorrow hidden
                    // Known limitation: only date values saved with a timezone offset (not UTC-midnight) can
                    // shift by a day when the filter timezone differs from the one they were stored in.
                    // UTC-midnight values (the current storage format) are correct in every timezone.
                    const method = filterValue[field] as FiltersRelativeDateMethod;
                    const range = FiltersRelativeDateUtility.getFilter(method) as { $gte?: Date; $lte?: Date; $lt?: Date };

                    if (range) {
                        const zone = method.timezone ?? 'utc';
                        const startOfDay = (value: any) => DateUtility.getDateTime(value, zone).startOf('day');
                        const taskDay = startOfDay(task[field]);
                        // $gte/$lte are inclusive instant bounds; $lt is an exclusive end, so the last
                        // included day is the day holding the instant just before it.
                        const lastIncludedDay = (end: any) => DateUtility.getDateTime(end, zone).minus({ milliseconds: 1 }).startOf('day');

                        match = (range.$gte == null || taskDay >= startOfDay(range.$gte))
                            && (range.$lte == null || taskDay <= startOfDay(range.$lte))
                            && (range.$lt == null || taskDay <= lastIncludedDay(range.$lt));
                    }
                    break;
                }

                case "daily":
                    {
                        const value = filterValue[field];
                        if (value === true) {
                            match = task[field] === value;
                        }
                        else {
                            match = !task[field];
                        }

                        break;
                    }

                case 'answered':
                    match = task[field] === filterValue[field];
                    break;

                case 'name':
                    match = task.name?.includes(filterValue[field]);
                    break;


                case "status":
                case 'target':
                case 'type': {
                    const arr = filterValue[field] as any[];
                    if (arr.length === 0) {
                        break;
                    }

                    match = arr.some(e => e._id === task[field]);
                    break;
                }
            }

            result.push(match);
        }

        return !result.some(e => !e);
    }
}
